import styled from 'styled-components';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isString } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { Dialog as MaterialDialog, Grid, Portal } from '@material-ui/core';
import { Alert, EmptyState, palette } from '@dev/ee-components';
import { TemplatesPreview } from '@dev/ee-modules';
import useMobile from '@/services/hooks/useMobile';
import { useHistoryBackActionContext } from '@/services/hooks/useHistoryBackAction';
import { ApplicationState } from '@/store';
import { PreviewOverlayProps } from './interface';

const Overlay = styled.div`
    background: ${palette.snow[1]};
    height: max-content;

    .MuiContainer-root {
        padding-top: 60px;
    }
    iframe {
        background: unset !important;
    }
`;

const AlertWrapper = styled.div`
    margin-bottom: 20px;
`;

const blockAllinputsCss = `
    <style type="text/css">
    input,label,textarea,button{
        pointer-events: none;
    }
    /* Disable the cursor on the close button */
    .editor-main-content-wrapper > *:first-child:before {
        cursor: default;
    }
    </style>
`;

export const PreviewOverlay = (props: PreviewOverlayProps): JSX.Element => {
    const { previewContent, onClose, fullWidthContent, isPreviewEditable, onEdit } = props;
    const { t } = useTranslation();
    const browser = useSelector((state: ApplicationState) => state.browser);
    const isMobile = useMobile();
    const historyBackAction = useHistoryBackActionContext();

    const templateWithExternalLinks = useMemo(() => {
        if (!isString(previewContent)) {
            return previewContent;
        }
        return previewContent
            ?.replace?.('</head>', `${blockAllinputsCss}</head>`)
            ?.replace(/action="[^"]*"/g, '')
            .replace?.(/<a(.*?)>/gi, '<a $1 target="_blank">');
    }, [previewContent]);

    useEffect(() => {
        historyBackAction?.pushBackCallback(props.onClose);
    }, []);

    const onCloseClick = (): void => {
        onClose(true);
    };

    const onEditClick = (): void => {
        onEdit?.();
    };

    const messageComponent = props.isMobileAlertVisible ? (
        <AlertWrapper>
            <Alert body={t('mobileformeditingtoolsnotavailable')} severity="info" />
        </AlertWrapper>
    ) : null;

    return (
        <Portal>
            <MaterialDialog open fullScreen>
                <Overlay>
                    <Grid container justifyContent="center" style={{ padding: '0px 16px' }}>
                        <Grid item xs={12} sm={12} style={{ maxWidth: 1170, paddingBottom: 16 }}>
                            <TemplatesPreview
                                fullWidthContent={fullWidthContent}
                                open
                                browser={browser}
                                isDrawerMenuFolded={false}
                                templateList={null}
                                templateHtml={templateWithExternalLinks}
                                templateName={props.name ?? ''}
                                isMobile={isMobile}
                                onCloseClick={onCloseClick}
                                emptyComponent={<EmptyState text={t('thereisnodatatodisplay')} />}
                                messageComponent={messageComponent}
                                isPreviewEditable={isPreviewEditable}
                                onEditClick={onEditClick}
                            />
                        </Grid>
                    </Grid>
                </Overlay>
            </MaterialDialog>
        </Portal>
    );
};
